import { gql } from 'graphql-tag';
import { pick, set } from 'lodash';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from '~/store';

export const masterVendorBioIT = {
  namespace: 'master/vendor-bioit'
};

const initialState = () => ({
  id: null,
  controller: null,
  data: {
    vendors: {
      count: 0,
      results: []
    }
  },
  table: {
    headers: [
      {
        text: 'No',
        align: 'center',
        sortable: false,
        value: 'id'
      },
      {
        text: 'BioIT Name',
        align: 'center',
        sortable: true,
        value: 'name'
      },
      {
        text: 'BioIT Type',
        align: 'center',
        sortable: false,
        value: 'type'
      },
      {
        text: 'Status',
        align: 'center',
        sortable: false,
        value: 'isActive'
      },
      {
        text: '',
        align: 'center',
        sortable: false,
        value: 'actions'
      }
    ],
    footer: {
      'items-per-page-options': [10, 25, 50, 100],
      'show-current-page': true,
      'show-first-last-page': true
    },
    filter: {
      keyword: ''
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['name'],
      sortDesc: [false],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: true
    }
  },
  query: {
    where: {
      name: ''
    },
    sort: {
      name: 'ASC'
    },
    offset: 0,
    limit: 10
  },
  loading: {
    findAll: false,
    findOne: false,
    create: false,
    update: false
  }
});

export const state = () => initialState();

export type MasterVendorBioITState = ReturnType<typeof state>;

export const getters: GetterTree<MasterVendorBioITState, RootState> = {
  getId(state: any) {
    return state.id;
  },
  getVendors: ({ data }) => data.vendors,
  getController(state: any) {
    return state.controller;
  },
  getTable: ({ table }) => table,
  getLoading: ({ loading }) => loading
};

export const mutations: MutationTree<MasterVendorBioITState> = {
  changeId: (state, id) => (state.id = id),
  changeTableFilter: (state, filter) => {
    Object.assign(state.table, { filter });
    Object.assign(state.query, {
      where: {
        name: filter.keyword
      }
    });
  },
  changeTableOptions: (state, options) => {
    Object.assign(state.table, { options });
    Object.assign(state.query, {
      sort: set({}, options.sortBy[0], options.sortDesc[0] ? 'DESC' : 'ASC'),
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage
    });
  },
  setVendors: (state, data: MasterVendorBioITState['data']['vendors']) =>
    (state.data.vendors = data),
  setLoading: (state, loading: MasterVendorBioITState['loading']) =>
    (state.loading = Object.assign({}, state.loading, loading)),
  changeController(state, controller: AbortController | null) {
    state.controller = controller;
  },
  resetState(state) {
    Object.assign(state, initialState());
  }
};

export const actions: ActionTree<MasterVendorBioITState, RootState> = {
  async findAll(
    { commit, state, dispatch },
    props?: { standalone: boolean; query: MasterVendorBioITState['query'] }
  ) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findAll: true });

      let variables = {
        ...state.query
      };

      if (props?.standalone)
        variables = {
          ...props.query
        };

      const { data } = await api.query({
        query: gql`
          query VendorBiotFindAll(
            $where: VendorBioItFilterInput
            $sort: VendorBioItSortInput
            $offset: Int
            $limit: Int
          ) {
            VendorBioIt {
              findAll(
                where: $where
                sort: $sort
                offset: $offset
                limit: $limit
              ) {
                count
                results {
                  id
                  name
                  type
                  isActive
                  vendorBioItIds
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const vendors = data?.VendorBioIt?.findAll;
      commit('setLoading', { findAll: false });
      if (props?.standalone) {
        return { status: true, message: null, data: vendors };
      } else {
        commit('setVendors', pick(vendors, ['count', 'results']));
      }
    } catch (error) {
      commit('setLoading', { findAll: false });
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      if (props?.standalone) {
        return { status: false, message: error, data: null };
      } else {
        commit('setVendors', { count: 0, results: [] });
      }
    }
  },

  async create(
    { commit },
    {
      form
    }: {
      form: {
        name: string;
        isActive: boolean;
      };
    }
  ) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;

    try {
      commit('setLoading', { create: true });

      await api.mutate({
        mutation: gql`
          mutation VendorBiotCreate($form: VendorBioItForm!) {
            VendorBioIt {
              create(form: $form) {
                id
              }
            }
          }
        `,
        variables: { form }
      });

      commit('setLoading', { create: false });
      return { status: true, message: '' };
    } catch (error) {
      commit('setLoading', { create: false });
      return { status: false, message: error };
    }
  },

  async update(
    { commit },
    {
      id,
      form
    }: {
      id: string;
      form: {
        id: string;
        name?: string;
        isActive?: boolean;
      };
    }
  ) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;

    try {
      commit('setLoading', { update: true });

      await api.mutate({
        mutation: gql`
          mutation VendorBiotUpdate($id: String!, $form: VendorBioItForm!) {
            VendorBioIt {
              update(id: $id, form: $form) {
                id
                name
                isActive
              }
            }
          }
        `,
        variables: { id, form }
      });

      commit('setLoading', { update: false });
      return { status: true, message: '' };
    } catch (error) {
      commit('setLoading', { update: false });
      return { status: false, message: error };
    }
  },

  /**
   * Creates a new AbortController object instance.
   */
  controllerInit({ commit, state }) {
    if (!state.controller) commit('changeController', new AbortController());
  },

  /**
   * Controller abort for canceled request.
   */
  controllerAbort({ commit, state }) {
    if (state.controller) {
      state.controller.abort();
      commit('changeController', null);
    }
  }
};
